import {ActionIcon, Button, Paper, Progress, TextInput} from "@mantine/core";
import {modals} from "@mantine/modals";
import {useContext, useMemo, useRef, useState} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import MessageContext from "../../context/MessageContext";
import api from "../../utils/api";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {DataTable} from "mantine-datatable";
import {CopyIcon, FileIcon, TrashIcon, MagnifyingGlassIcon} from "@phosphor-icons/react";
import Page from "../../components/Page/Page.tsx";

interface UserFile {
  id: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  cdnUrl: string;
  uploadDate: number;
}

interface StorageInfo {
  used: number;
  max: number;
  available: number;
  percentage: number;
}

interface FilesResponse {
  files: UserFile[];
  storage: StorageInfo;
}

const PAGE_SIZE = 10;

export default function MediaUpload() {
  const {success, error} = useContext(MessageContext);
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch user files
  const {data: filesData, isLoading} = useQuery<FilesResponse>({
    queryKey: ['user-files'],
    queryFn: async () => {
      const {data} = await api.get('/files');
      return data;
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const {data} = await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    },
    onSuccess: () => {
      success('File uploaded successfully');
      queryClient.invalidateQueries({queryKey: ['user-files']});
      setUploading(false);
    },
    onError: (err: any) => {
      error(err.response?.data?.error || 'Failed to upload file');
      setUploading(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      await api.delete(`/files/${fileId}`);
    },
    onSuccess: () => {
      success('File deleted successfully');
      queryClient.invalidateQueries({queryKey: ['user-files']});
    },
    onError: (err: any) => {
      error(err.response?.data?.error || 'Failed to delete file');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    uploadMutation.mutate(formData);
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  };

  const handleDelete = (file: UserFile) => {
    modals.openConfirmModal({
      title: 'Confirm Delete',
      children: <p>Are you sure you want to delete "{file.originalFilename}"?</p>,
      labels: {confirm: 'Delete', cancel: 'Cancel'},
      confirmProps: {color: 'red'},
      onConfirm: () => deleteMutation.mutate(file.id),
    });
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    success('URL copied to clipboard');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const filteredFiles = useMemo(() => {
    if (!filesData?.files) return [];
    if (!globalFilter) return filesData.files;
    const lower = globalFilter.toLowerCase();
    return filesData.files.filter(f =>
      f.originalFilename?.toLowerCase().includes(lower) ||
      f.cdnUrl?.toLowerCase().includes(lower)
    );
  }, [filesData?.files, globalFilter]);

  const paginatedFiles = useMemo(() => {
    return filteredFiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredFiles, page]);

  const urlTemplate = (rowData: UserFile) => {
    return (
      <div className="flex gap-2 align-items-center">
        <a href={rowData.cdnUrl} target="_blank" rel="noopener noreferrer" className="text-sm">
          {rowData.cdnUrl}
        </a>
        <Button
          leftSection={<CopyIcon size={16} />}
          size="compact-sm"
          variant="subtle"
          onClick={() => copyToClipboard(rowData.cdnUrl)}
          title="Copy URL"
        />
      </div>
    );
  };

  const previewTemplate = (rowData: UserFile) => {
    if (rowData.mimeType.startsWith('image/')) {
      return (
        <img
          src={rowData.cdnUrl}
          alt={rowData.originalFilename}
          style={{width: '50px', height: '50px', objectFit: 'cover'}}
        />
      );
    }
    return <FileIcon size={32} />;
  };

  const actionsTemplate = (rowData: UserFile) => {
    return (
      <ActionIcon
        color="red"
        variant="subtle"
        onClick={() => handleDelete(rowData)}
        loading={deleteMutation.isPending}
      >
        <TrashIcon />
      </ActionIcon>
    );
  };

  return (
    <Page>
      <title>Image Hosting</title>
      <Row align="center" className="mb-4">
        <Col>
          <h1>Image Hosting</h1>
        </Col>
      </Row>

      <Row mb={4} gap={2}>
        <Col grow={1}>
          <Paper>
            <h2 className="mt-0">Storage Usage</h2>
            {filesData && (
              <div>
                <Row className="mb-2">
                  <Col>
                    <strong>Used:</strong> {formatFileSize(filesData.storage.used)}
                  </Col>
                  <Col>
                    <strong>Available:</strong> {formatFileSize(filesData.storage.available)}
                  </Col>
                  <Col>
                    <strong>Total:</strong> {formatFileSize(filesData.storage.max)}
                  </Col>
                </Row>
                <Progress value={filesData.storage.percentage}/>
              </div>
            )}
          </Paper>
        </Col>
        <Col basis='50%'>
          <Paper>
            <h2 className="mt-0">Upload File</h2>
            <p className="text-sm text-color-secondary mb-3">
              Allowed types: JPG, PNG, GIF, WEBP | Images are automatically optimized to WebP format
            </p>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileChange}
              style={{display: 'none'}}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || uploadMutation.isPending}
            >
              {uploading ? "Uploading..." : "Choose File"}
            </Button>
          </Paper>
        </Col>
      </Row>

      <Paper>
        <h2 className="mt-0">Your Files</h2>
        {isLoading ? (
          <p>Loading files...</p>
        ) : filesData && filesData.files.length > 0 ? (
          <>
            <div className="mb-3">
              <TextInput
                leftSection={<MagnifyingGlassIcon size={16} />}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search files..."
                className="w-full"
              />
            </div>
            <DataTable
              records={paginatedFiles}
              idAccessor="id"
              totalRecords={filteredFiles.length}
              recordsPerPage={PAGE_SIZE}
              page={page}
              onPageChange={setPage}
              noRecordsText="No files found"
              columns={[
                {accessor: 'preview', title: 'Preview', width: 80, render: (row) => previewTemplate(row)},
                {accessor: 'originalFilename', title: 'Filename'},
                {accessor: 'fileSize', title: 'Size', render: (row) => formatFileSize(row.fileSize)},
                {accessor: 'uploadDate', title: 'Uploaded', render: (row) => formatDate(row.uploadDate)},
                {accessor: 'cdnUrl', title: 'URL', render: (row) => urlTemplate(row)},
                {accessor: 'actions', title: 'Actions', width: 100, render: (row) => actionsTemplate(row)},
              ]}
            />
          </>
        ) : (
          <p className="text-color-secondary">No files uploaded yet</p>
        )}
      </Paper>
    </Page>
  );
}
